<script>
  import { onMount } from "svelte"
  import { push, querystring } from "svelte-spa-router"
  import Footer from "../../../../Shared/footer.svelte"
  import Navbar from "../../../../Shared/Navbar/index.svelte"
  import UploadArea from "./Components/uploadArea.svelte"
  import { validateFile } from "../../../Publish/BulkUpload/Script/bulkUpload"
  import notify from "../../../../Script/notify"
  import { applySilhouette } from "./Script/silhoutte"
  import { queryStringToJSON } from "../../../../Script/queryString"
  import { getRequest, postRequest } from "../../../../Script/apiMethods"
  import ApplicationState from "../../../../Stores/applicationStates"
  import Loader from "../../../../Shared/Slots/loader.svelte"

  let showLoader = false

  let loaderMsg = "Fetching Seal Details"

  /**
   * @type {HTMLCanvasElement}
   */
  let canvas

  /**
   * @type {CanvasRenderingContext2D}
   */
  let ctx

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  let closeNotification = {
    close() {},
  }

  /**
   * @typedef {Object} maxNotification
   * @property {Function} close - Closes the notification.
   */
  let maxNotification = {
    close() {},
  }

  /**
   * @typedef {Object} invalidNotification
   * @property {Function} close - Closes the notification.
   */
  let invalidNotification = {
    close() {},
  }

  /**
   * @type {Number} indicates the maximum characters for the template name
   */
  const MAX_CHARS = 25

  const textClr = "#7CCD9C"

  let seal = {
    semi: false,
    outerTop: "Print2B Tech Private Limited",
    outerBottom: "ESTABLISHED 2019",
    innerTop: "DOCCHAIN",
    innerBottom: "AUG 8, 2019",
    middleValue: "P2B",
    middleLogo: "",
  }

  let sealName = ""

  let file = ""

  let imageBlob

  let query = {}

  let timer

  let isText = true

  onMount(async () => {
    try {
      // Prevent execution if unauthorized (because pop() is asynchronous)
      if (!$ApplicationState.pdfEnabled || !$ApplicationState.msignToken) return

      query = queryStringToJSON($querystring)

      if (query.id) {
        const { name, ...rest } = await fetchSealDetails(query.id)

        sealName = name
        seal = { ...rest }
      }
    } catch (error) {
      console.error(error)
      const info = "PAGE: Edit Seal Design\nEVENT: onMount"
      notify.danger(error, info)
    }
  })

  const fetchSealDetails = async (id) => {
    try {
      const REQ_URL = `/api/pdf-signing/v2/seal/${id}`
      const CONFIG = {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      }

      const { seal } = await getRequest(REQ_URL, CONFIG)

      return seal
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  $: handleSealChange(canvas, seal)

  const handleSealChange = () => {
    if (!canvas) return
    drawCanvas()
  }

  const drawCanvas = () => {
    try {
      ctx = canvas.getContext("2d")

      // ctx.fillStyle = "#ffffff"
      // ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      // Draw outer circle
      ctx.beginPath()
      ctx.lineWidth = 4
      ctx.strokeStyle = textClr

      const outerRadius = 100 - ctx.lineWidth / 2 - 5 // subtract extra pixel to move in
      ctx.arc(centerX, centerY, outerRadius + 0.5, 0, 2 * Math.PI)

      ctx.stroke()

      // Draw Inner circle
      ctx.beginPath()
      ctx.lineWidth = 4
      ctx.strokeStyle = textClr
      ctx.arc(centerX, centerY, 60, 0, 2 * Math.PI)
      ctx.stroke()

      // Draw name in the center
      if (seal.middleValue) {
        ctx.font = "bold 20px ELEPHNT"
        ctx.fillStyle = textClr
        ctx.textAlign = "center"
        ctx.fillText(seal.middleValue, centerX, centerY + 10)
      }
      if (seal.middleLogo) drawImageToCanvas(seal.middleLogo)

      if (seal.semi) {
        drawArcText(seal.innerTop, 40, centerX, centerY, true, true)
        drawArcText(seal.innerBottom, 50, centerX, centerY, false, true)
        drawArcText(seal.outerTop, 75, centerX, centerY, true)
        drawArcText(seal.outerBottom, 85, centerX, centerY, false)
      } else {
        drawFullRoundedText(seal.innerTop, 40, centerX, centerY, true)
        drawFullRoundedText(seal.outerTop, 75, centerX, centerY, false)
      }
    } catch (error) {
      console.error(error)
      const info = "PAGE: Edit Seal Design\nEVENT: While redrawing canvas based on seal details change"
      notify.danger(error, info)
    }
  }

  const drawImageToCanvas = (image) => {
    try {
      const ratioValue = 0.2

      const canvasRatioWidth = canvas.width * ratioValue // use 80% of canvas size
      const canvasRatioHeight = canvas.height * ratioValue

      // Calculate the scaling factor while preserving the aspect ratio
      const scale = Math.min(canvasRatioWidth / image.width, canvasRatioHeight / image.height)

      const imgWidth = image.width * scale
      const imgHeight = image.height * scale

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      const offsetX = centerX - imgWidth / 2
      const offsetY = centerY - imgHeight / 2

      ctx.drawImage(image, offsetX, offsetY, imgWidth, imgHeight)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const drawFullRoundedText = (text, radius, centerX, centerY, isInner) => {
    try {
      ctx.save()
      ctx.font = "16px ELEPHNT"
      ctx.fillStyle = textClr
      ctx.textAlign = "center"
      ctx.translate(centerX, centerY)
      let angleStep = isInner ? 0.25 : 0.15

      const chars = text.split("")
      const totalAngle = (chars.length - 1) * angleStep
      const startAngle = -totalAngle / 2

      for (let i = 0; i < chars.length; i++) {
        const char = chars[i]
        const angle = startAngle + i * angleStep

        // Skip character if it exceeds 180 degrees (±π/2 from top or bottom center)
        if (Math.abs(angle) > Math.PI) continue

        ctx.save()
        ctx.rotate(angle)
        ctx.translate(0, -radius)

        ctx.fillText(char, 0, 0)
        ctx.restore()
      }

      ctx.restore()
    } catch (error) {
      throw error
    }
  }

  const drawArcText = (text, radius, centerX, centerY, isTop = true, isInner = false) => {
    try {
      ctx.save()
      ctx.font = "16px ELEPHNT"
      ctx.fillStyle = textClr
      ctx.textAlign = "center"
      ctx.translate(centerX, centerY)
      let angleStep = isInner ? 0.25 : 0.15

      const chars = text.split("")
      const totalAngle = (chars.length - 1) * angleStep
      const startAngle = isTop ? -totalAngle / 2 : Math.PI - totalAngle / 2

      for (let i = 0; i < chars.length; i++) {
        const char = chars[i]
        const angle = startAngle + i * angleStep

        // Skip character if it exceeds 180 degrees (±π/2 from top or bottom center)
        if (seal.semi) {
          const angleFromCenter = isTop ? angle : angle - Math.PI
          if (Math.abs(angleFromCenter) > Math.PI / 2) continue
        }

        ctx.save()
        ctx.rotate(isTop ? angle : -angle)
        ctx.translate(0, -radius)

        if (!isTop) {
          ctx.rotate(Math.PI) // Flip bottom text
        }

        ctx.fillText(char, 0, 0)
        ctx.restore()
      }

      ctx.restore()
    } catch (error) {
      throw error
    }
  }

  const handleImageUpload = async (e) => {
    try {
      // console.log(e.target.files)
      if (!e.target.files[0]) return

      const MAX_FILE_SIZE = 2 * 1024 * 1024

      if (e.target.files[0].size > MAX_FILE_SIZE) throw "Maximum File Size exceeded"

      file = await validateFile(e.target.files[0])

      imageBlob = URL.createObjectURL(file)

      seal.middleLogo = await applySilhouette(imageBlob)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Edit Seal Design\nEVENT: While validating uploaded image to place inside the seal"
      closeNotification = notify.danger(error, info)
      e.target.value = file = null
    }
  }

  /**
   * @description  function to check not to exceed max characters
   */
  const exceedsmaxCharacters = () => {
    if (sealName.length >= MAX_CHARS) {
      maxNotification.close()

      //returning values only in range below max characters
      setTimeout(() => {
        sealName = sealName.slice(0, MAX_CHARS)
      }, 100)

      maxNotification = notify.info(`Seal name should not be more than ${MAX_CHARS} characters`)
    }
  }

  /**
   * function to check for given pattern to template name
   * @param e - to capture key event
   */
  const checkSpecialCharsName = (e) => {
    const info = "PAGE: Edit Seal Design\nEVENT: Validating seal name onKeypress/ onBlur/ onPaste"
    try {
      // Destructure the type and key properties from the event object
      let { type, key } = e

      // Regular expression to match allowed characters
      const ALLOWED_CHARS_REGEX = /^[A-Za-z0-9\s-()+&.]+$/

      // Check if it's a keypress event and the pressed key is not allowed
      if (type === "keypress" && !ALLOWED_CHARS_REGEX.test(key)) {
        e.preventDefault()

        sealName += key

        invalidNotification.close()

        // Prevent the default keypress behavior if the pressed key is not allowed
        // Show a danger notification indicating the allowed characters
        invalidNotification = notify.danger("Only letters, numbers, and special characters -()+&. are allowed.", info)
        setTimeout(() => {
          sealName = sealName.slice(0, -1)
        }, 100)
      } else if (type === "paste" || type === "blur") {
        let pastedText
        if (type === "blur") {
          pastedText = sealName
        } else {
          // Get the pasted text from the clipboard
          let clipboardData = e.clipboardData || window.clipboardData
          pastedText = clipboardData.getData("text/plain")
        }

        setTimeout(() => {
          invalidNotification.close()
          // Check if the pasted text contains any invalid special characters
          if (!ALLOWED_CHARS_REGEX.test(pastedText)) {
            // Remove any characters that are not allowed
            let sanitizedText = pastedText.replace(/[^A-Za-z0-9\s-()+&.]/g, "").trim()

            // Check if the sanitized text is the same as the original pasted text
            if (sanitizedText !== pastedText) {
              invalidNotification = notify.danger("Invalid special characters found. Only letters, numbers, and special characters -()+&. are allowed.", info)
            }

            // Set the input value to the sanitized text
            sealName = sanitizedText
          }
          exceedsmaxCharacters()
        }, 500)
      }
    } catch (error) {
      console.error(error)
      notify.danger(error, info)
    }
  }

  /**
   * Converts a canvas element to a Blob object.
   *
   * @param {HTMLCanvasElement} canvas - The canvas element to be converted.
   * @returns {Promise<Blob>} A Promise that resolves with the generated Blob object.
   */
  const canvasToBlob = (canvas) => {
    /**
     * @typedef {Blob} Blob - Represents a binary large object (Blob) that can store binary data.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Blob Blob}
     */

    return new Promise((resolve) => {
      /**
       * The `toBlob` method of the canvas element is used to convert it to a Blob.
       * @param {Blob} blob - The generated Blob object.
       */
      canvas.toBlob((blob) => {
        // Once the Blob is created, the Promise is resolved with the Blob object.
        resolve(blob)
      }, "image/png")
    })
  }

  const handleSubmit = async () => {
    try {
      showLoader = true
      loaderMsg = "Saving Seal"

      sealName = sealName.trim()

      if (!sealName) throw "Kindly enter Seal Name"

      let blobObject = await canvasToBlob(canvas)

      // Create a new File object with the Blob and provide a default filename and type
      let image = new File([blobObject], `${sealName + "-" + Date.now()}.png`, {
        type: "image/png",
      })

      await postRequest(
        "/api/pdf-signing/v2/seal",
        { name: sealName, image, ...seal },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "msign-org-token": $ApplicationState.msignToken,
          },
        }
      )

      notify.success(`Seal named as ${sealName} saved successfully.`)

      push("/signing/seal")
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Edit Seal Design\nEVENT: Error occurred while creating seal."
      closeNotification = notify.danger(error, info)
    } finally {
      showLoader = false
      loaderMsg = "Creating Seal"
    }
  }
</script>

{#if showLoader}
  <Loader>
    <span class="flex">
      {@html loaderMsg}
    </span>
  </Loader>
{/if}

<div class="relative min-h-screen w-full overflow-hidden bg-[#f7f7f7] pb-24 dark:bg-bodyblack">
  <Navbar />

  <div class="px-6 pb-12 pt-6">
    <div class="relative mx-auto h-full w-full max-w-[920px] rounded-md bg-[#f7f7f7] dark:bg-bodyblack">
      <!-- file Temp ListToggle Switch -->
      <div class="mx-auto w-full">
        <button
          on:click={() => {
            push("/signing/seal")
          }}
          style="text-decoration: none;"
          class="mb-1 hidden w-16 cursor-pointer select-none text-base font-medium text-gray-200 hover:text-blue-600 focus-visible:text-blue-600 focus-visible:outline-none active:text-blue-500 sm:mb-0 md:block"
        >
          <span>
            <i class="fa-solid fa-reply" />
            <span>&nbsp;Back&nbsp;</span>
          </span>
        </button>

        <div class="flex flex-col justify-between md:flex-row">
          <div class="mx-auto mt-3 flex flex-col items-center justify-center mobile:mt-0 md:ml-0 md:items-start lg:mx-0">
            <div class="select-none text-xl font-bold text-black dark:text-white lg:text-2xl xl:text-2xl">Edit Seal Design</div>
          </div>

          <div class="mt-3 flex items-center justify-center gap-3 md:mt-0">
            <div data-tooltip="Back to List" tooltip-position="top" class="block md:hidden">
              <button type="button" class="flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-full border border-[#a0a1a580] bg-white px-2 text-sm font-medium text-gray-900 hover:bg-blue-600 hover:text-white focus:outline-none focus-visible:bg-blue-600 focus-visible:text-white focus-visible:outline-none active:bg-blue-500 dark:bg-lightgray dark:text-white dark:hover:bg-blue-500">
                <i class="fa-solid fa-reply" />
              </button>
            </div>

            <button on:click={handleSubmit} type="button" class="flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-green-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-600 hover:text-white focus:outline-none disabled:cursor-not-allowed disabled:bg-[#f7f7f7]/50 disabled:text-gray-900/50 dark:bg-green-500 dark:text-white dark:hover:bg-green-600 sm:w-auto">
              <span>
                <i class="fa-solid fa-floppy-disk" />
              </span>
              <span class="select-none">&nbsp;Save&nbsp;Seal</span>
            </button>
          </div>
        </div>
      </div>

      <div class="relative mt-3 flex min-h-[470px] flex-col justify-between gap-6 rounded-md bg-white p-6 text-white dark:bg-mdGray md:flex-row">
        <div class="flex h-fit w-full md:w-2/5">
          <canvas bind:this={canvas} width="205" height="205" class="w-full max-w-md" style="background: url(assets/img/grid.png);" />
        </div>

        <div class="flex w-full flex-col gap-3 md:w-3/5">
          <div class="flex w-full items-center justify-center">
            <div class="w-full space-y-3 rounded-lg bg-white dark:bg-transparent dark:bg-[#19191c]">
              <div class="space-y-1">
                <div class="text-base font-medium text-gray-200">Name</div>
                <input
                  on:keypress={exceedsmaxCharacters}
                  on:keypress={(e) => {
                    clearTimeout(timer)
                    timer = setTimeout(checkSpecialCharsName(e), 50)
                  }}
                  on:blur={checkSpecialCharsName}
                  on:paste={checkSpecialCharsName}
                  on:paste={exceedsmaxCharacters}
                  bind:value={sealName}
                  required
                  type="text"
                  class="block w-full appearance-none rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 placeholder-gray-200 outline-none invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white"
                  placeholder="Name of the Seal"
                />
              </div>

              <div class="flex items-center gap-3">
                <button
                  on:click={() => {
                    seal.semi = !seal.semi
                  }}
                  type="button"
                  class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent {seal.semi ? 'bg-gray-200' : 'bg-blue-500'} transition-colors duration-200 ease-in-out"
                  role="switch"
                  aria-checked="false"
                >
                  <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                  <span aria-hidden="true" class="pointer-events-none inline-block h-5 w-5 {seal.semi ? 'translate-x-0' : 'translate-x-5'}  transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" />
                </button>
                <span class="text-gray-800 dark:text-white">{!seal.semi ? "Full Circle Text" : "Semi Circle text"}</span>
              </div>

              {#if seal.semi}
                <div class="space-y-1">
                  <label for="topText">Top Text</label>
                  <input autocomplete="off" bind:value={seal.outerTop} type="text" id="topText" name="topText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>
                <div class="space-y-1">
                  <label for="topText">Inner Top Text</label>
                  <input autocomplete="off" bind:value={seal.innerTop} type="text" id="topText" name="topText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>
                <div class="space-y-1">
                  <label for="topText">Inner Bottom Text</label>
                  <input autocomplete="off" bind:value={seal.innerBottom} type="text" id="topText" name="topText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>

                <div class="space-y-1">
                  <label for="bottomText">Bottom Text</label>
                  <input autocomplete="off" bind:value={seal.outerBottom} type="text" id="bottomText" name="bottomText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>
              {:else}
                <div class="space-y-1">
                  <label for="topText">Outer Text</label>
                  <input autocomplete="off" bind:value={seal.outerTop} type="text" id="topText" name="topText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>

                <div class="space-y-1">
                  <label for="bottomText">Inner Text</label>
                  <input autocomplete="off" bind:value={seal.innerTop} type="text" id="bottomText" name="bottomText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>
              {/if}

              <div class="flex items-center gap-3">
                <button
                  on:click={() => {
                    isText = !isText
                    if (isText) seal.middleValue = "Amanda"
                    else file = seal.middleValue = seal.middleLogo = ""
                  }}
                  type="button"
                  class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent {isText ? 'bg-blue-500' : 'bg-gray-200'} transition-colors duration-200 ease-in-out"
                  role="switch"
                  aria-checked="false"
                >
                  <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                  <span aria-hidden="true" class="pointer-events-none inline-block h-5 w-5 {isText ? 'translate-x-5' : 'translate-x-0'}  transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" />
                </button>
                <span class="text-gray-800 dark:text-white">{isText ? "Middle Text" : "Image"}</span>
              </div>
              {#if isText}
                <div class="space-y-1">
                  <label for="middleText">Middle Text</label>
                  <input autocomplete="off" bind:value={seal.middleValue} type="text" id="middleText" name="middleText" class="w-full rounded border border-[#a0a1a580] bg-white py-2 px-4 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-transparent dark:text-white" />
                </div>
              {:else if !file}
                <UploadArea on:change={handleImageUpload} />
              {:else}
                <div class="flex w-full items-center gap-3 rounded-md border border-stone-700">
                  <div class="group relative flex w-full justify-center p-3">
                    <img src={imageBlob} alt="headerImage" class="aspect-auto w-1/3 rounded-md" />
                    <div type="button" class="absolute inset-0 hidden items-center justify-center bg-[#000000cc] text-2xl group-hover:flex group-hover:text-red-500">
                      <div class="relative">
                        <button class="h-full w-full rounded-md border border-white bg-transparent bg-white px-3 py-1 text-base font-normal text-white group-hover:font-bold group-hover:text-blue-500">Change File</button>
                        <input type="file" on:change={handleImageUpload} class="absolute inset-0 z-10 cursor-pointer opacity-0" accept="image/*" />
                      </div>
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>

      <button on:click={handleSubmit} type="button" class="mt-3 ml-auto flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-green-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-600 hover:text-white focus:outline-none disabled:cursor-not-allowed disabled:bg-[#f7f7f7]/50 disabled:text-gray-900/50 dark:bg-green-500 dark:text-white dark:hover:bg-green-600 sm:w-auto">
        <span>
          <i class="fa-solid fa-floppy-disk" />
        </span>
        <span class="select-none">&nbsp;Save&nbsp;Seal</span>
      </button>
    </div>
  </div>
  <Footer />
</div>

<style lang="postcss">
  canvas {
    font-family: "ELEPHNT";
    /* font-family: "Outfit", sans-serif; */
  }
  label {
    @apply text-base font-medium text-gray-200;
  }
</style>
